import React, { useEffect, useState } from 'react';
import { api } from '../api';
import { useAuth } from '../AuthContext';

const VIEWS = { START: 'start', CREATE: 'create' };

export default function Setup() {
  const { appUser, setAppUser, logout } = useAuth();
  const [view, setView]               = useState(VIEWS.START);
  const [accountName, setAccountName] = useState('');
  const [locationName, setLocationName] = useState('');
  const [address, setAddress]         = useState('');
  const [zipCode, setZipCode]         = useState('');
  const [saving, setSaving]           = useState(false);
  const [error, setError]             = useState('');
  const [invites, setInvites]         = useState([]);
  const [inviteError, setInviteError] = useState('');

  useEffect(() => { api.get('/invites/mine').then(setInvites).catch(() => {}); }, []);

  // Joining changes everything the app loaded for this user, so start fresh.
  const acceptInvite = async (id) => {
    setInviteError('');
    try {
      await api.post(`/invites/${id}/accept`);
      window.location.reload();
    } catch (err) {
      setInviteError(err.message);
    }
  };

  const declineInvite = async (id) => {
    setInviteError('');
    try {
      await api.post(`/invites/${id}/decline`);
      setInvites(list => list.filter(i => i.id !== id));
    } catch (err) {
      setInviteError(err.message);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!accountName.trim() || !locationName.trim() || !address.trim() || !zipCode.trim()) return;
    setSaving(true);
    setError('');
    try {
      const { account, location } = await api.post('/setup', {
        account_name: accountName.trim(),
        location_name: locationName.trim(),
        address: address.trim(),
        zip_code: zipCode.trim(),
      });
      setAppUser(u => ({
        ...u,
        location_id: location.id,
        is_admin: true,
        account_id: account.id,
        account,
        location,
      }));
    } catch (err) {
      setError(err.message);
      setSaving(false);
    }
  };

  if (view === VIEWS.CREATE) {
    return (
      <div className="min-h-dvh bg-slate-50 dark:bg-slate-900 flex items-center justify-center p-6">
        <div className="w-full max-w-md">
          <button onClick={() => setView(VIEWS.START)} className="text-sm text-slate-400 hover:text-slate-600 flex items-center gap-1 mb-6 transition-colors">
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back
          </button>
          <h1 className="text-lg font-semibold text-slate-900 dark:text-white mb-1">Create your account</h1>
          <p className="text-sm text-slate-500 dark:text-slate-400 mb-6">
            Set up your dealership account and first location. You'll be the account admin.
          </p>

          <form onSubmit={handleCreate} className="space-y-4">
            <div>
              <label className="label">Business / Dealership Name</label>
              <input className="input" placeholder="Acme Motors" value={accountName}
                onChange={e => setAccountName(e.target.value)} required autoFocus />
            </div>
            <div>
              <label className="label">Location Name</label>
              <input className="input" placeholder="Main Lot" value={locationName}
                onChange={e => setLocationName(e.target.value)} required />
            </div>
            <div>
              <label className="label">Street Address</label>
              <input className="input" placeholder="123 Main St, Salt Lake City, UT" value={address}
                onChange={e => setAddress(e.target.value)} required />
              <p className="mt-1 text-xs text-slate-400 dark:text-slate-500">Each physical lot is its own location. You can add more later.</p>
            </div>
            <div>
              <label className="label">ZIP Code</label>
              <input className="input" placeholder="84101" value={zipCode}
                onChange={e => setZipCode(e.target.value)} required />
            </div>
            {error && <p className="text-xs text-red-600">{error}</p>}
            <button type="submit" disabled={saving} className="btn-primary w-full">
              {saving ? 'Setting up…' : 'Create Account'}
            </button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-dvh bg-slate-50 dark:bg-slate-900 flex items-center justify-center p-6">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div>
            <img src="/logo.svg" alt="Lane Bids" className="h-10 mx-auto dark:hidden" />
            <img src="/logo-white.svg" alt="Lane Bids" className="h-10 mx-auto hidden dark:block" />
          </div>
          <p className="text-slate-500 dark:text-slate-400 mt-2 text-sm">
            Welcome, {appUser?.fname || 'there'}. You're not assigned to an account yet.
          </p>
        </div>

        <div className="space-y-3">
          {invites.map(inv => (
            <div key={inv.id} className="card p-5 border-blue-200 dark:border-blue-800">
              <p className="text-sm font-semibold text-slate-800 dark:text-slate-100">
                {inv.account_name} invited you to join
              </p>
              <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">Lot: {inv.location_name}</p>
              <div className="flex gap-2 mt-3">
                <button onClick={() => acceptInvite(inv.id)} className="btn-primary text-sm">Accept</button>
                <button onClick={() => declineInvite(inv.id)} className="btn-secondary text-sm">Decline</button>
              </div>
            </div>
          ))}
          {inviteError && <p className="text-xs text-red-600">{inviteError}</p>}

          <button
            onClick={() => setView(VIEWS.CREATE)}
            className="w-full card p-5 text-left hover:border-blue-300 dark:hover:border-blue-600 transition-colors group"
          >
            <div className="flex items-center gap-4">
              <div className="w-10 h-10 bg-blue-50 dark:bg-blue-900/30 rounded-lg flex items-center justify-center shrink-0 group-hover:bg-blue-100 dark:group-hover:bg-blue-900/50 transition-colors">
                <svg className="w-5 h-5 text-blue-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                </svg>
              </div>
              <div className="flex-1">
                <p className="text-sm font-semibold text-slate-800 dark:text-slate-100">Create a new account</p>
                <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">Set up your dealership and first location. You'll become the admin.</p>
              </div>
              <svg className="w-4 h-4 text-slate-300 dark:text-slate-600 group-hover:text-blue-400 transition-colors" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </div>
          </button>

          <div className="card p-5 bg-slate-50 dark:bg-slate-800/50">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 bg-slate-100 dark:bg-slate-700 rounded-lg flex items-center justify-center shrink-0 mt-0.5">
                <svg className="w-5 h-5 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
              </div>
              <div className="flex-1">
                <p className="text-sm font-semibold text-slate-700 dark:text-slate-300">Waiting for an invite?</p>
                <p className="text-xs text-slate-500 dark:text-slate-400 mt-1 leading-relaxed">
                  Joining someone else's dealership is invite-only — there's no self-signup for that.
                  Ask your account admin to add you under <span className="font-medium">Settings → Team</span> (by
                  your email, {appUser?.email || 'the one you just registered with'}). Their invite will
                  show up here for you to accept — refresh this page once they've sent it.
                </p>
              </div>
            </div>
          </div>
        </div>

        <p className="text-center mt-6">
          <button onClick={logout} className="text-xs text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 transition-colors">
            Sign out
          </button>
        </p>
      </div>
    </div>
  );
}
